import $ from 'jquery';
import { nodeConfig } from '@/mixins';
import { mod, afterTransition } from '@/helpers/utils';
import { Swap } from '@/plugins/swap';

const NAMESPACE = 'nav-toggle';
const DEFAULT_SELECTOR = '[data-js-nav-toggle-group]';
const DEFAULT_SETTINGS = {
	//
};

class NavToggle {
	constructor(node, config) {
		this.$node = $(node);
		this.config = config;

		this.$trigger = this.$node.find('[data-js-nav-toggle-button]');
		this.$panel = this.$node.find('[data-js-nav-toggle-panel]');
		this.$link = this.$node.find('[data-js-nav-toggle-link]');
		this.$items = this.$node.find('[data-js-nav-toggle-item]');
		this.$close = this.$node.find('[data-js-nav-toggle-close]');
		this.linkFocusDirection = 'first';
	}

	closeOtherMainNavToggles() {
		const $container = this.$node.closest('.header__main-nav');
		if (!$container.length) return;
	
		const $others = $container
			.find('[data-js-nav-toggle-group]')
			.not(this.$node);
	
		$others.each((i, el) => {
			const $trigger = $(el).find('[data-js-nav-toggle-button]');
	
			if ($trigger.attr('aria-expanded') === 'true') {
				Swap.deactivate($trigger[0]);
			}
		});
	}

	openPopup() {
		let panelStyle = window
			.getComputedStyle(this.$panel[0], null)
			.getPropertyValue('transition');

		this.$panel.removeAttr('inert');

		if (panelStyle != 'all 0s ease 0s') {
			afterTransition(this.$panel[0], 'opacity', () => {
				this.linkFocusDirection == 'first'
					? this.$link.first().trigger('focus')
					: this.$link.last().trigger('focus');
			});
		} else {
			this.linkFocusDirection == 'first'
				? this.$link.first().trigger('focus')
				: this.$link.last().trigger('focus');
		}
	}

	closePopup() {
		this.$panel.attr('inert', '');
	}

	onTriggerActivate() {
		this.closeOtherMainNavToggles();
		this.openPopup();
	}

	onTriggerDeactivate() {
		this.closePopup();
	}

	focusFirst() {
		this.$items.first().find('[data-js-nav-toggle-link]').trigger('focus');
	}

	focusLast() {
		this.$items.last().find('[data-js-nav-toggle-link]').trigger('focus');
	}

	onTriggerKeydown(event) {
		let keyCode = event.key.toLowerCase();

		if (this.$trigger.attr('aria-expanded') == 'true') return;

		if (!['arrowup', 'arrowdown'].includes(keyCode)) return;

		event.preventDefault();

		let lookup = {
			arrowup: () => {
				this.linkFocusDirection = 'last';
				Swap.activate(this.$trigger[0]);
				return;
			},
			arrowdown: () => {
				this.linkFocusDirection = 'first';
				Swap.activate(this.$trigger[0]);
				return;
			}
		};

		if (keyCode in lookup) {
			lookup[keyCode]();
		}
	}

	onTriggerClick() {
		this.focusFirst();
	}

	onPanelKeydown(event) {
		let keyCode = event.key.toLowerCase();

		if (!['tab', 'escape', 'home', 'end', 'arrowup', 'arrowdown'].includes(keyCode)) return;

		if (!['tab'].includes(keyCode)) event.preventDefault();

		let $focusedElement = $(':focus');
		let $focusedItem = $focusedElement.closest('[data-js-nav-toggle-item]');
		let focusedIndex = this.$items.index($focusedItem);

		let lookup = {
			tab: () => {
				Swap.deactivate(this.$trigger[0]);
			},
			escape: () => {
				Swap.deactivate(this.$trigger[0]);
			},
			home: () => {
				this.focusFirst();
			},
			end: () => {
				this.focusLast();
			},
			arrowup: () => {
				let prevIndex = mod(focusedIndex - 1, this.$items.length);
				this.$items.eq(prevIndex).find('[data-js-nav-toggle-link]').trigger('focus');
			},
			arrowdown: () => {
				let nextIndex = mod(focusedIndex + 1, this.$items.length);
				this.$items.eq(nextIndex).find('[data-js-nav-toggle-link]').trigger('focus');
			}
		};

		if (keyCode in lookup) {
			lookup[keyCode]();
		}
	}

	bindUI() {
		this.$trigger.on({
			keydown: this.onTriggerKeydown.bind(this),
			click: this.onTriggerClick.bind(this)
		});

		Swap.on(this.$trigger[0], 'activate', this.onTriggerActivate.bind(this));
		Swap.on(this.$trigger[0], 'deactivate', this.onTriggerDeactivate.bind(this));

		this.$panel.on({
			keydown: this.onPanelKeydown.bind(this)
		});

		this.$close.on('click', () => {
			Swap.deactivate(this.$trigger[0]);
		});
	}

	setup() {
		new Swap(this.$trigger[0], {
			target: this.$node[0],
			triggerAttributes: [
				{
					attribute: 'aria-expanded',
					on: 'true',
					off: 'false'
				}
			],
			focusOnDeactivate: true,
		});

		this.$trigger.attr({
			'aria-haspopup': 'true',
		});
		this.$panel.attr('inert', '');
	}

	init() {
		this.setup();
		this.bindUI();
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	let listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		let config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		let module = new NavToggle(node, config);

		module.init();

		return module;
	});
}
